// Turns a checkout error response into the sentence the buyer sees.
//
// The shop checkout answers failures with `{ type, result, errors }`, where
// `result` is the reason -- a translated server message, a validation
// sentence, a gateway rejection. The extractor this replaces never looked at
// `result`, so it fell through to the generic string every time, and the log
// filled with "An error occurred. Please try again." rows that each had a
// usable explanation sitting one field away.

export const GENERIC_CHECKOUT_ERROR = "An error occurred. Please try again.";

// A payload field is only usable as a message if it is a non-blank string.
// `result` is sometimes an object -- Stripe's requires_action response nests
// the payment intent there -- and stringifying that shows the buyer
// "[object Object]".
function usableMessage(value) {
  if (typeof value !== "string") return null;

  return value.trim() ? value : null;
}

function fromErrors(errors) {
  if (!errors) return null;
  if (Array.isArray(errors)) return usableMessage(errors.filter(Boolean).join(", "));
  if (typeof errors === "object") {
    return usableMessage(
      Object.values(errors)
        .flat()
        .filter((value) => typeof value === "string" && value.trim())
        .join(", ")
    );
  }

  return usableMessage(errors);
}

export function extractCheckoutError(data, fallback = GENERIC_CHECKOUT_ERROR) {
  if (!data) return fallback;
  if (typeof data === "string") return usableMessage(data) ?? fallback;

  return (
    usableMessage(data.error) ??
    usableMessage(data.message) ??
    usableMessage(data.result) ??
    // Payment failures from the order pipeline (`general_failure`,
    // `order_payment_incomplete`) nest the sentence as `result.message`.
    usableMessage(data.result?.message) ??
    fromErrors(data.errors) ??
    fallback
  );
}
